import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Fade,
} from '@mui/material';
import {
  CheckCircle,
} from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

export interface AnimalRemovedData {
  resumo?: string;
  url?: string;
}

export interface AnimalRemovedSuccessViewProps {
  animalRemovedData: AnimalRemovedData;
}

const AnimalRemovedSuccessView: React.FC<AnimalRemovedSuccessViewProps> = ({
  animalRemovedData,
}) => {
  const { resumo } = animalRemovedData;

  // Extract animal name and lot name from resumo
  const extractInfo = (resumoText: string) => {
    const match = resumoText.match(/(\d+) animais removidos do lote '([^']+)': (\w+)/);
    if (match) {
      return {
        count: match[1],
        lotName: match[2],
        animalName: match[3]
      };
    }
    return { count: '1', lotName: 'Lote', animalName: 'Animal' };
  };

  const { count, lotName, animalName } = extractInfo(resumo || '');

  return (
    <Box sx={{ 
      p: 4, 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      minHeight: '400px',
      textAlign: 'center'
    }}>
      <Fade in timeout={600}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Success Icon */}
          <Avatar
            sx={{
              width: 120,
              height: 120,
              bgcolor: DESIGN_TOKENS.colors.brand.primary,
              mb: 3,
              boxShadow: `0 8px 32px ${DESIGN_TOKENS.colors.brand.surface}`,
            }}
          >
            <CheckCircle sx={{ fontSize: '4rem', color: 'white' }} />
          </Avatar>

          {/* Success Message */}
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              color: DESIGN_TOKENS.colors.brand.primary,
              mb: 2
            }}
          >
            Animal Removido com Sucesso!
          </Typography>

          <Typography 
            variant="h6" 
            sx={{ 
              color: DESIGN_TOKENS.colors.text.primary,
              mb: 1,
              fontWeight: 600
            }}
          >
            {resumo || 'Animal foi removido do lote com sucesso.'}
          </Typography>

          {/* Animal Details Card */}
          <Card sx={{ 
            mt: 3, 
            mb: 4, 
            minWidth: 300,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: `2px solid ${DESIGN_TOKENS.colors.brand.surface}`
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: '2rem',
                    height: '2rem',
                    backgroundColor: DESIGN_TOKENS.colors.organization.farm,
                    mask: 'url(/vaca.png) no-repeat center',
                    maskSize: 'contain',
                    WebkitMask: 'url(/vaca.png) no-repeat center',
                    WebkitMaskSize: 'contain',
                  }}
                />
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.organization.farm }}>
                    Animal {animalName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                    Removido do lote {lotName}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                    Lote:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {lotName}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                    Animal:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {animalName}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                    Quantidade:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {count} animal{count !== '1' ? 's' : ''}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Additional Info */}
          <Typography 
            variant="body2" 
            sx={{ 
              color: DESIGN_TOKENS.colors.text.secondary,
              mt: 3,
              fontStyle: 'italic'
            }}
          >
            O animal foi removido do lote e pode ser gerenciado individualmente.
          </Typography>
        </Box>
      </Fade>
    </Box>
  );
};

export default AnimalRemovedSuccessView;